import * as Sentry from '@sentry/react';
import { createFileLogger } from './logger';
import { parseJwtPayload } from './jwtUtils';
import {
  ANALYTICS_SESSION_KEY,
  ANALYTICS_SESSION_ACTIVITY_KEY,
  analytics,
} from '../services/analytics/analytics';
import { resetAuthCompleteCoordinator } from '../components/Auth/authCompleteCoordinator';

const log = createFileLogger('LOGOUT_UTILS');

const ACCOUNTS_INDEX_KEY = 'river_accounts';
const ACCOUNT_PREFIX = 'river_account_';

/**
 * Remove the currently-active account from the multi-account switcher.
 * Must be called BEFORE the authToken is cleared so we can still read the userId.
 */
function removeActiveAccountFromSwitcher(): void {
  try {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) return;

    const payload = parseJwtPayload(authToken);
    const userId = payload?.sub;
    if (!userId) return;

    localStorage.removeItem(`${ACCOUNT_PREFIX}${userId}`);

    const raw = localStorage.getItem(ACCOUNTS_INDEX_KEY);
    const ids: string[] = raw ? JSON.parse(raw) : [];
    const updated = ids.filter(id => id !== userId);
    if (updated.length > 0) {
      localStorage.setItem(ACCOUNTS_INDEX_KEY, JSON.stringify(updated));
    } else {
      localStorage.removeItem(ACCOUNTS_INDEX_KEY);
    }

    log.log(`🧹 [LOGOUT] Removed account ${userId} from multi-account switcher`);
  } catch (e) {
    // Non-fatal — don't block the logout flow
    log.warn('[LOGOUT] Failed to remove account from switcher:', e);
  }
}

/**
 * Auth-related storage keys — single source of truth for logout cleanup.
 *
 * Add every key here when you introduce it. Keys are split by storage type so
 * clearAllAuthState can iterate them correctly.
 */
export const AUTH_STORAGE_KEYS = {
  // ── localStorage ──────────────────────────────────────────────────────────
  localStorage: {
    // Auth tokens
    tokens: ['authToken', 'id_token', 'refresh_token'],

    // Pending OAuth context (set before OAuth popup, consumed after)
    pending: [
      'pendingFunnelId',
      'pendingPersona',
      'pendingPageGroup',
      'pendingPageSlug',
      'pendingRedirectTo',
      'pendingTemplateId',
      'pendingIsSignup',
      'pendingWorkflowContext',
      'pendingWorkflowContext_timestamp',
      'pre_signup_onboarding_answers',
    ],

    // Session / redirect data
    session: [
      'redirectAfterLogin',
      'lastAuthRedirect',
      'userSpaceId',
      'userWelcomeDocId',
      'signupEmail',
      // Persisted dataspace context (zustand persist key from
      // dataspaceContextStore). Must be cleared on logout — without this,
      // a same-browser User-A → User-B login would rehydrate User A's
      // orgId for the brief window before App.tsx's /user/context fetch
      // returns, causing one wasted REST round-trip and a flicker of
      // "no access" state from the backend's is_org_member check.
      'dataspace-context',

      // Persisted `dismissed` flag from whatCanRiverDoTipStore. Must be
      // cleared on logout — otherwise User A dismissing the "What can River
      // do?" tip would permanently hide it for User B on the same shared
      // browser, even though User B has never seen it.
      'what-can-river-do-tip-storage',
    ],

    // GalBox status
    galbox: ['galbox-inactive', 'galbox-inactive-name'],
  },

  // ── sessionStorage ────────────────────────────────────────────────────────
  sessionStorage: [
    'pendingRedirectTo',
    'pendingTemplateId',
    'landing_hero_context',
    'onboarding_source',
    // Analytics session keys — must be cleared so the post-logout session
    // gets a fresh ID and does not inherit the pre-logout session.
    ANALYTICS_SESSION_KEY,
    ANALYTICS_SESSION_ACTIVITY_KEY,
  ],
} as const;

/**
 * Clear ALL auth-related data from localStorage and sessionStorage.
 */
export function clearAllAuthState(): void {
  log.log('🧹 [LOGOUT] Starting comprehensive auth state cleanup...');

  // In-memory (not storage-backed) — reset so a subsequent sign-in within the
  // same SPA session isn't permanently blocked from either side handling it.
  resetAuthCompleteCoordinator();

  let clearedCount = 0;

  // Clear all localStorage keys
  const lsKeys = Object.values(AUTH_STORAGE_KEYS.localStorage).flat() as string[];
  lsKeys.forEach(key => {
    if (localStorage.getItem(key) !== null) {
      localStorage.removeItem(key);
      clearedCount++;
      log.log(`  ✓ Cleared localStorage: ${key}`);
    }
  });

  // Clear Auth0 SDK cache entries (@@auth0spajs@@::...) which live in localStorage
  // when cacheLocation='localstorage'. The Auth0 SDK manages its own key names so
  // we sweep by prefix rather than maintaining a hardcoded key.
  Object.keys(localStorage)
    .filter(key => key.startsWith('@@auth0spajs@@'))
    .forEach(key => {
      localStorage.removeItem(key);
      clearedCount++;
      log.log(`  ✓ Cleared Auth0 SDK cache: ${key}`);
    });

  // Clear all sessionStorage keys
  AUTH_STORAGE_KEYS.sessionStorage.forEach(key => {
    if (sessionStorage.getItem(key) !== null) {
      sessionStorage.removeItem(key);
      clearedCount++;
      log.log(`  ✓ Cleared sessionStorage: ${key}`);
    }
  });

  log.log(`🧹 [LOGOUT] Cleanup complete - cleared ${clearedCount} items`);
}

/**
 * Compute the localStorage queue keys belonging to the currently-active user.
 *
 * Mirrors offlineEntityQueue._getStorageKey: the keyspace is
 *   `river_offline_entity_queue_<sub>`  (namespaced — preferred)
 *   `river_offline_entity_queue`        (fallback when no auth token)
 *
 * Returns BOTH so we can wipe whichever this session was actually using —
 * but never the namespaced keys of OTHER signed-in users (the multi-account
 * switcher keeps those alive across a single-user logout).
 */
function _activeUserQueueKeys(): string[] {
  const keys: string[] = ['river_offline_entity_queue'];
  try {
    const token = localStorage.getItem('authToken');
    if (token) {
      const payload = parseJwtPayload(token);
      if (payload?.sub) {
        keys.push(`river_offline_entity_queue_${payload.sub}`);
      }
    }
  } catch { /* ignore — fallback key alone is fine */ }
  return keys;
}

/**
 * Drop in-memory entity caches and wipe every River-owned IndexedDB database.
 * Awaited by the logout flow so the navigation does not race the wipe.
 *
 * @param queueKeysToWipe localStorage keys for the active user's offline
 *   queue, captured BEFORE clearAllAuthState removes the auth token.
 * @returns the count of IDB databases successfully deleted.
 */
async function wipeClientCaches(queueKeysToWipe: string[]): Promise<number> {
  const [
    { useSpaceEntityStore },
    { useEntityByIdCache },
    { useGlobalEntityStore },
    { wipeRiverIDBs },
  ] = await Promise.all([
    import('../stores/spaceEntityStore'),
    import('../stores/entityByIdCache'),
    import('../stores/globalEntityStore'),
    import('../services/idb/idbCleanup'),
  ]);
  useSpaceEntityStore.getState().clearAll();
  useEntityByIdCache.getState().clear();
  // Drop the in-memory global cache (Spaces/Integrations/MCPConnections from
  // the outgoing user). The IDB-backed snapshot is removed by wipeRiverIDBs;
  // this resets the React-visible state so a same-tab login as a different
  // user does not see the previous user's spaces in the brief moment before
  // navigation completes.
  await useGlobalEntityStore.getState().reset();

  // Drop ONLY the active user's offline create queue. Anything queued was
  // never sent to the server, so it is acceptable to drop on explicit logout;
  // re-logging in as the same user would otherwise replay queued items
  // against a workspace state that may have moved on (parents deleted, etc.).
  let removed = 0;
  for (const key of queueKeysToWipe) {
    if (localStorage.getItem(key) !== null) {
      localStorage.removeItem(key);
      removed += 1;
    }
  }
  if (removed > 0) {
    log.log(`🧹 [LOGOUT] Cleared ${removed} offline entity queue entries`);
  }

  return wipeRiverIDBs();
}

/**
 * Perform a complete logout:
 * 1. Clear all localStorage/sessionStorage
 * 2. Drop in-memory entity caches + wipe River IDB databases (awaited)
 * 3. Optionally call Auth0 logout
 * 4. Redirect to home
 *
 * Returns a promise that resolves once the wipe has completed (navigation will
 * have started by then). Callers may fire-and-forget or await — the navigation
 * is unconditional.
 */
export async function performCompleteLogout(options: {
  auth0Logout?: (params: any) => void;
  hasAuth0User: boolean;
}): Promise<void> {
  log.log('🚪 [LOGOUT] Starting complete logout process...');

  // Step 0: Capture the active user's per-account state (queue keys, account ID)
  // BEFORE clearAllAuthState wipes the authToken — the JWT `sub` claim is the
  // only way to derive these keys.
  const queueKeysToWipe = _activeUserQueueKeys();
  removeActiveAccountFromSwitcher();

  // Step 1: Reset analytics facade (emits session_ended, rotates session ID).
  // Must run before clearAllAuthState so the authToken is still available for
  // the session_ended beacon payload.
  analytics.reset();

  // Clear the Sentry user context so the post-logout session isn't attributed
  // to the previous user.
  Sentry.setUser(null);

  // Step 2: Clear all auth state
  clearAllAuthState();

  // Step 2b: Tell the Electron main process to drop its in-process token cache.
  // Without this, the main process keeps using the stale token for heartbeats
  // until it expires (intermittent 401s, no observable hard failure) because
  // `pushTokenToMain` in index.tsx no longer pushes null values to avoid
  // clobbering the cache during renderer-reload races. Browser builds skip
  // this silently — riverOS.auth is only injected by the Electron preload.
  try {
    (window as any).riverOS?.auth?.clearToken?.();
  } catch (e) {
    log.warn('[LOGOUT] Failed to clear Electron auth-token cache:', e);
  }

  // Step 3: Wipe client caches + IDB. Awaited so the redirect below does not
  // race the deletes — losing the wipe would mean cached entity data survives
  // a logout, which is contrary to the security expectation.
  try {
    const deleted = await wipeClientCaches(queueKeysToWipe);
    log.log(`🧹 [LOGOUT] Wiped River IDBs (${deleted} databases)`);
  } catch (e) {
    log.warn('[LOGOUT] Failed to wipe River IDBs:', e);
  }

  // Step 4: Auth0 logout if applicable
  if (options.hasAuth0User && options.auth0Logout) {
    log.log('🚪 [LOGOUT] Performing Auth0 logout...');
    options.auth0Logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  } else {
    // Step 5: Redirect to home (Auth0 logout handles redirect itself)
    log.log('🚪 [LOGOUT] Redirecting to home...');
    window.location.href = '/';
  }
}

